/**
 * useSectionTiers — per-tenant per-section tier gating (Shane,
 * 2026-06-12).
 *
 * The platform admin sets which tier variants (Base / Plus /
 * Customised) each tenant gets per section, in the Sidebar-sections
 * modal. Every section page passes its catalog key here and renders
 * ONLY the enabled tier buttons:
 *
 *   const { enabledTiers, tier, setTier, showTierBar } =
 *     useSectionTiers('stock_management');
 *   ...
 *   {showTierBar && TIERS.filter(t => enabledTiers.has(t.key)).map(...)}
 *
 * Rules:
 *   • a key ABSENT from the tenant's map = the default, Base only;
 *   • the selected tier is clamped to the enabled set (highest enabled
 *     wins when the previous selection is switched off);
 *   • showTierBar is false when only ONE tier is enabled — a
 *     single-tier customer just sees their section, no tier chrome.
 *
 * Build-mode safety: while the profile is still loading, all three
 * tiers are treated as enabled so nothing flashes hidden-then-shown
 * for tenants that have them.
 */

import { useEffect, useMemo, useState } from 'react';
import { useCurrentTenant } from './useCurrentTenant';
import { useTenantHoldsSection } from './tenantSections';

export type SectionTierKey = 'base' | 'plus' | 'customised';

const TIER_ORDER: SectionTierKey[] = ['base', 'plus', 'customised'];

export interface SectionTiersState {
  /** The tiers the platform admin has enabled for this section. */
  enabledTiers: Set<SectionTierKey>;
  /** Current selection — always a member of enabledTiers (once loaded). */
  tier: SectionTierKey;
  /**
   * 2026-06-28 — the tenant's EFFECTIVE entitlement: the highest enabled tier
   * (Customised > Plus > Base), independent of any on-page selection. Sections
   * with no on-page tier selector (the sidebar-dropdowns model, where Platform
   * Admin sets the tier and Plus overrides Base) read THIS instead of `tier`.
   * `tier` defaults to Base and only promotes when Base is disabled, so it
   * cannot represent "Plus, with Base also enabled". Fails closed to Base while
   * the tenant profile is still loading (no flash of higher-tier content).
   */
  effectiveTier: SectionTierKey;
  setTier: (t: SectionTierKey) => void;
  /** False when only one tier is enabled — hide the tier bar. */
  showTierBar: boolean;
}

export function useSectionTiers(sectionKey: string): SectionTiersState {
  const tenantQ = useCurrentTenant();
  const [tier, setTier] = useState<SectionTierKey>('base');

  const enabledTiers = useMemo<Set<SectionTierKey>>(() => {
    if (tenantQ.isLoading || !tenantQ.data) {
      // Profile not loaded yet — treat all as enabled so the bar
      // doesn't flash in and out for fully-enabled tenants.
      return new Set(TIER_ORDER);
    }
    const map = tenantQ.data.sectionTiers ?? {};
    const entry = map[sectionKey];
    if (!Array.isArray(entry) || entry.length === 0) {
      // Absent key = the default: Base only.
      return new Set<SectionTierKey>(['base']);
    }
    const valid = TIER_ORDER.filter((t) => entry.includes(t));
    return new Set(valid.length > 0 ? valid : ['base']);
  }, [tenantQ.isLoading, tenantQ.data, sectionKey]);

  // Clamp the selection: if the current tier is switched off, snap to
  // the HIGHEST enabled tier (the best experience they're entitled to).
  useEffect(() => {
    if (enabledTiers.has(tier)) return;
    const highest = [...TIER_ORDER].reverse().find((t) => enabledTiers.has(t)) ?? 'base';
    setTier(highest);
  }, [enabledTiers, tier]);

  const guardedSetTier = (t: SectionTierKey): void => {
    if (enabledTiers.has(t)) setTier(t);
  };

  const effectiveTier: SectionTierKey =
    tenantQ.isLoading || !tenantQ.data
      ? 'base'
      : ([...TIER_ORDER].reverse().find((t) => enabledTiers.has(t)) ?? 'base');

  return {
    enabledTiers,
    tier: enabledTiers.has(tier)
      ? tier
      : ([...TIER_ORDER].reverse().find((t) => enabledTiers.has(t)) ?? 'base'),
    effectiveTier,
    setTier: guardedSetTier,
    showTierBar: enabledTiers.size > 1,
  };
}

/**
 * Does this tenant have a section at all?
 *
 * Switchboard Builder stopped being the Customised tier of Enquiries on
 * 2026-08-22 and became its own section, so the screens that used to ask
 * `effectiveTier === 'customised'` ask this instead. Same question the sidebar
 * asks, so the nav entry and the buttons inside the page cannot disagree.
 *
 * IT DID DISAGREE (fixed 2026-09-30). This was a bare
 * `selected.includes(sectionKey)`, which is right for a configured tenant and
 * wrong for every other one: an EMPTY `selectedSections` means "nobody has set
 * this tenant up, show everything", not "holds nothing". So on an unconfigured
 * tenant the sidebar drew the section and this said it was absent — exactly
 * what the sentence above promises cannot happen. It now delegates to the one
 * rule, in lib/tenantSections.ts, which matches the API's `onShowList`.
 *
 * Kept as a named export because callers already import it from here; the
 * behaviour is the sidebar's, not a second opinion.
 */
export function useHasSection(sectionKey: string): boolean {
  return useTenantHoldsSection(sectionKey);
}
